import { ListItem } from '@mui/material';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from 'src/components/atoms';
import { NotificationTextContainer } from 'src/components/moleculesLegacy/ActualNotifications/ActualNotifications.styles';
import { StyledNavBarIcon } from 'src/components/organisms/Navbar/BaseNavbar.styles';
import { NotificationsQuery } from 'src/graphql';
import type { ArrayElement } from 'src/types/types';
import { timeFromNow } from 'src/utils/time';
import {
  CreatedAtContainer,
  NotificationActions,
  NotificationContainer,
  NotificationContainerColumn,
  NotificationHeader,
  NotificationHeaderRow,
  NotificationItemContainer,
  StyledDivider,
  StyledIconButton,
  StyledLink,
  StyledListItemHeader,
  StyledListItemText,
} from './NotificationItem.styles';

export interface NotificationItemProps {
  notification: ArrayElement<NotificationsQuery['notifications']>;
  title: string;
  subtitle?: string;
  secondaryText: string;
  link: string;
  handleMenuClose: React.MouseEventHandler<HTMLLIElement>;
  handleNotificationClose: (notificationId: number) => void;
  handleNotificationRead: (notificationId: number) => void;
  icon: string;
  lastNotification?: boolean;
  notificationsOpen?: boolean;
  newNavEnabled?: boolean;
  helpLinkUrl?: string;
}

export const NotificationItem = (props: NotificationItemProps) => {
  const { t } = useTranslation();
  return (
    <NotificationItemContainer>
      <ListItem
        id={`notification-${props.notification.id}`}
        onClick={props.handleMenuClose}
        style={{ padding: 0 }}
        data-testid={'report-notification'}
      >
        <NotificationContainer
          onClick={() => props.handleNotificationRead(props.notification.id)}
          id={`notification-${props.notification.id}-container`}
          notificationsOpen={props.notificationsOpen}
          newNavEnabled={props.newNavEnabled}
        >
          <NotificationContainerColumn>
            <StyledNavBarIcon className={`uil ${props.icon}`} size="20px" />
          </NotificationContainerColumn>
          <NotificationTextContainer>
            <NotificationHeaderRow>
              <StyledLink to={props.link} data-testid={`notification-link`}>
                <NotificationHeader>
                  <StyledListItemHeader hasButton={Boolean(props.helpLinkUrl)}>
                    {props.title}
                  </StyledListItemHeader>
                  {props.helpLinkUrl ? (
                    <Button
                      id={`help-link-${props.notification.id}`}
                      data-testid={'help-link-button'}
                      variant="secondary"
                      size="small"
                      onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
                        event.stopPropagation();
                        event.preventDefault();
                        window.open(props.helpLinkUrl as string, '_blank');
                      }}
                    >
                      {t('general.navbar.notificationsDrawer.learnMore')}
                    </Button>
                  ) : null}
                </NotificationHeader>
                {props.subtitle && (
                  <StyledListItemText>{props.subtitle}</StyledListItemText>
                )}
                <StyledListItemText>{`- ${props.secondaryText}`}</StyledListItemText>
              </StyledLink>
              <NotificationActions>
                <StyledIconButton
                  id={`hide-notification-${props.notification.id}`}
                  data-testid={'hide-notification-button'}
                  aria-owns={'material-appbar'}
                  aria-haspopup="true"
                  onClick={(event) => {
                    event.stopPropagation();
                    props.handleNotificationClose(props.notification.id);
                  }}
                >
                  <StyledNavBarIcon
                    className="uil uil-multiply"
                    size="20px"
                    style={{ margin: 0 }}
                  />
                </StyledIconButton>
              </NotificationActions>
            </NotificationHeaderRow>
            <CreatedAtContainer>
              {timeFromNow(parseInt(props.notification.createdAt))}
            </CreatedAtContainer>
          </NotificationTextContainer>
        </NotificationContainer>
      </ListItem>
      {!props.lastNotification && <StyledDivider $width="350px" />}
    </NotificationItemContainer>
  );
};
